import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowDown, ArrowUp, ArrowUpDown, Columns3 } from 'lucide-react';
import { Button } from 'src/features/tyre-management/components/ui/button';
import { Checkbox } from 'src/features/tyre-management/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from 'src/features/tyre-management/components/ui/table';
import {
  Popover, PopoverContent, PopoverTrigger,
} from 'src/features/tyre-management/components/ui/popover';
import StateBadge from 'src/features/tyre-management/components/StateBadge';
import { APIService } from 'src/api/api';

export const VEHICLE_COLUMNS = [
  { id: 'vehicle', label: 'Vehicle', required: true, sort: 'registration_num' },
  { id: 'asset_code', label: 'Asset code', sort: 'asset_code' },
  { id: 'site_name', label: 'Site', sort: 'site_name' },
  { id: 'group_name', label: 'Group', sort: 'group_name' },
  { id: 'type', label: 'Movement type', sort: 'type' },
  { id: 'active', label: 'Active', sort: 'active' },
  { id: 'tyre_management_enabled', label: 'Tyre tracking', sort: 'tyre_management_enabled' },
  { id: 'action', label: '', required: true },
];

export const useHiddenColumns = () => {
  const [hidden, setHidden] = useState([]);

  useEffect(() => {
    let mounted = true;
    APIService.getTableColumnPreference('vehicles').then((response) => {
      if (!mounted || !response?.success) return;
      setHidden(response.result?.hidden_columns || []);
    });
    return () => { mounted = false; };
  }, []);

  const toggle = (columnId) => {
    setHidden((current) => {
      const next = current.includes(columnId)
        ? current.filter((id) => id !== columnId)
        : [...current, columnId];
      APIService.updateTableColumnPreference('vehicles', { hidden_columns: next });
      return next;
    });
  };

  return { hidden, toggle };
};

export const ColumnVisibilityMenu = ({ hidden, toggle, globalEnabled }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button type="button" variant="outline" className="h-10 shrink-0" aria-label="Choose visible columns">
        <Columns3 />
        Columns
      </Button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-64">
      <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.14em] text-slate-500">Visible columns</p>
      <div className="grid gap-2">
        {VEHICLE_COLUMNS.filter((column) => (
          !column.required
          && (globalEnabled || column.id !== 'tyre_management_enabled')
        )).map((column) => (
          <label key={column.id} className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Checkbox
              checked={!hidden.includes(column.id)}
              onCheckedChange={() => toggle(column.id)}
              aria-label={column.label}
            />
            {column.label}
          </label>
        ))}
      </div>
    </PopoverContent>
  </Popover>
);

ColumnVisibilityMenu.propTypes = {
  hidden: PropTypes.arrayOf(PropTypes.string).isRequired,
  toggle: PropTypes.func.isRequired,
  globalEnabled: PropTypes.bool,
};

const SortIcon = ({ state }) => {
  if (state === 'ascending') return <ArrowUp className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />;
  if (state === 'descending') return <ArrowDown className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />;
  return <ArrowUpDown className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" />;
};

SortIcon.propTypes = { state: PropTypes.string };

const cellFor = (column, vehicle, {
  onOpen, onOpenTyres, onEnableTyres, globalEnabled,
}) => {
  if (column.id === 'vehicle') {
    return (
      <button type="button" className="tm-plain-button text-left" onClick={() => onOpen(vehicle.id)}>
        <p className="vl-mono text-sm font-extrabold tracking-wide text-primary hover:underline">
          {vehicle.registration_num}
        </p>
        <p className="mt-1 text-xs text-slate-500">{vehicle.model || 'Model not recorded'}</p>
      </button>
    );
  }
  if (column.id === 'asset_code') {
    return <span className="vl-mono font-semibold text-slate-800">{vehicle.asset_code || '—'}</span>;
  }
  if (column.id === 'site_name') return vehicle.site_name || '—';
  if (column.id === 'group_name') return vehicle.group_name || '—';
  if (column.id === 'type') return vehicle.type || '—';
  if (column.id === 'active') {
    return (
      <StateBadge
        value={vehicle.active ? 'active' : 'inactive'}
        label={vehicle.active ? 'Yes' : 'No'}
      />
    );
  }
  if (column.id === 'tyre_management_enabled') {
    return (
      <StateBadge
        value={vehicle.tyre_management_enabled ? 'enabled' : 'disabled'}
        label={vehicle.tyre_management_enabled ? 'On' : 'Off'}
      />
    );
  }
  if (column.id === 'action') {
    return (
      <div className="flex items-center justify-end gap-1">
        <Button size="sm" variant="ghost" onClick={() => onOpen(vehicle.id)}>
          View
        </Button>
        {globalEnabled && vehicle.tyre_management_enabled && onOpenTyres && (
          <Button size="sm" variant="outline" onClick={() => onOpenTyres(vehicle.id)}>
            Manage tyres
          </Button>
        )}
        {globalEnabled && !vehicle.tyre_management_enabled && onEnableTyres && (
          <Button size="sm" variant="outline" onClick={() => onEnableTyres(vehicle.id)}>
            Turn on tracking
          </Button>
        )}
      </div>
    );
  }
  return null;
};

const VehicleDataTable = ({
  rows, ordering, onSort, onOpen, onOpenTyres, onEnableTyres, hidden, globalEnabled, updating,
}) => {
  const visible = useMemo(
    () => VEHICLE_COLUMNS.filter((column) => (
      (globalEnabled || column.id !== 'tyre_management_enabled')
      && (column.required || !hidden.includes(column.id))
    )),
    [globalEnabled, hidden]
  );

  const handleSort = (column) => {
    if (!column.sort) return;
    const current = ordering || '';
    if (current === column.sort) onSort(`-${column.sort}`);
    else if (current === `-${column.sort}`) onSort('');
    else onSort(column.sort);
  };

  const sortState = (column) => {
    if (!column.sort) return undefined;
    if (ordering === column.sort) return 'ascending';
    if (ordering === `-${column.sort}`) return 'descending';
    return 'none';
  };

  return (
    <Table className="min-w-[1080px]" aria-busy={updating || undefined}>
      <TableHeader>
        <TableRow>
          {visible.map((column) => (
            <TableHead key={column.id} aria-sort={sortState(column)}>
              {column.sort ? (
                <button
                  type="button"
                  className="tm-plain-button inline-flex items-center gap-1.5 uppercase tracking-wider text-slate-500"
                  onClick={() => handleSort(column)}
                >
                  {column.label}
                  <SortIcon state={sortState(column)} />
                </button>
              ) : column.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((vehicle) => (
          <TableRow
            key={vehicle.id}
            className="vl-row"
            tabIndex={0}
            onClick={() => onOpen(vehicle.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen(vehicle.id);
              }
            }}
          >
            {visible.map((column) => (
              <TableCell
                key={column.id}
                onClick={column.id === 'action' || column.id === 'vehicle'
                  ? (event) => event.stopPropagation()
                  : undefined}
              >
                {cellFor(column, vehicle, {
                  onOpen, onOpenTyres, onEnableTyres, globalEnabled,
                })}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

VehicleDataTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  ordering: PropTypes.string,
  onSort: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onOpenTyres: PropTypes.func,
  onEnableTyres: PropTypes.func,
  hidden: PropTypes.arrayOf(PropTypes.string).isRequired,
  globalEnabled: PropTypes.bool,
  updating: PropTypes.bool,
};

export default VehicleDataTable;
